/**
 * Download a workspace file through a short-lived signed asset URL minted
 * with `disposition: "attachment"` — any file type, served with a
 * Content-Disposition that saves it under its own name. Fetch the bytes before
 * saving a local Blob so failures can be reported without cross-origin navigation.
 */
import type {
  AssetCreateUrlInput,
  AssetCreateUrlResult,
  EnvironmentId,
  ScopedThreadRef,
} from "@amoreg/contracts";
import { type AtomCommandResult } from "@amoreg/client-runtime/state/runtime";
import * as Cause from "effect/Cause";
import { AsyncResult } from "effect/unstable/reactivity";

import { saveFileDownload } from "./saveFileDownload";
import { resolveAssetUrl } from "~/assets/assetUrls";

export async function downloadWorkspaceFile<AssetError>(input: {
  readonly threadRef: ScopedThreadRef;
  readonly filePath: string;
  readonly httpBaseUrl: string;
  readonly createAssetUrl: (input: {
    readonly environmentId: EnvironmentId;
    readonly input: AssetCreateUrlInput;
  }) => Promise<AtomCommandResult<AssetCreateUrlResult, AssetError>>;
}): Promise<AtomCommandResult<void, AssetError>> {
  const assetResult = await input.createAssetUrl({
    environmentId: input.threadRef.environmentId,
    input: {
      resource: {
        _tag: "workspace-file",
        threadId: input.threadRef.threadId,
        path: input.filePath,
      },
      disposition: "attachment",
    },
  });
  if (assetResult._tag === "Failure") {
    return AsyncResult.failure(assetResult.cause);
  }
  const assetUrl = resolveAssetUrl(input.httpBaseUrl, assetResult.value.relativeUrl);
  if (assetUrl === null) {
    return AsyncResult.failure(
      Cause.die(new Error("The environment returned an invalid asset URL.")),
    );
  }
  try {
    await saveFileDownload(assetUrl, input.filePath.split(/[\\/]/).pop() || "download");
    return AsyncResult.success(undefined);
  } catch (error) {
    return AsyncResult.failure(Cause.die(error));
  }
}
